<template>

  <div class="p-3">
    <div class="row" style="width: 100%">
      <div class="col-7">
        <div class="card mb-4">
          <div class="card-header">Dados de faturamento</div>
          <div class="card-body">
            <div v-if="hasCustomer">
              <div class="row mb-3">
                <div class="col-3"><b>Nome</b></div>
                <div class="col-9">{{ billingResume.customer.name }}</div>
              </div>
              <div class="row mb-3">
                <div class="col-3"><b>E-mail</b></div>
                <div class="col-9">{{ billingResume.customer.email }}</div>
              </div>
              <div class="row mb-3">
                <div class="col-3"><b>Telefone</b></div>
                <div class="col-9">({{ billingResume.customer.phone_prefix }}) {{ billingResume.customer.phone }}</div>
              </div>
              <div class="row mb-3">
                <div class="col-3"><b>CNPJ</b></div>
                <div class="col-9">{{ billingResume.customer.register_number }}</div>
              </div>
              <div class="row mb-3">
                <div class="col-3"><b>Endereço</b></div>
                <div class="col-9">{{ address }}</div>
              </div>
              <router-link
                :to="{ path: '/billing/steps/details' }">
                {{ $t('Editar dados de faturamento') }}
                </router-link>
            </div>
            <router-link
              v-if="!hasCustomer"
              :to="{ path: '/billing/steps/details' }">
              {{ $t('Informar dados de faturamento') }}
              </router-link>
          </div>
        </div>

        <div class="card">
          <div class="card-header">Métodos de pagamento</div>
          <ul class="list-group list-group-flush">
            <li
              v-if="payableWith.includes('credit_card')"
              class="list-group-item">
              <div class="mb-2"><b>Cartão de crédito (Automático)</b></div>
              <div
                v-if="hasDefaultPaymentMethod"
                class="mb-3">
                <div>{{ billingResume.default_paymethod.holder_name }}</div>
                <div>{{ billingResume.default_paymethod.display_number }} ({{ billingResume.default_paymethod.brand }})</div>
              </div>
              <router-link
                :to="{ path: '/billing/steps/payment' }">
                {{ hasDefaultPaymentMethod ? $t('Editar método de pagamento') : $t('Informar método de pagamento') }}
                </router-link>
            </li>
            <li
              v-if="payableWith.includes('bank_slip') && !billingResume.has_default_paymethod"
              class="list-group-item">
              <div class="mb-2"><b>Boleto ou PIX</b></div>
              <div>Você poderá pagar por boleto ou PIX, assim que a fatura for gerada.</div>
            </li>
          </ul>
        </div>
      </div>
      <div class="col-5">
        <div class="card mb-3">
          <div class="card-header">
            Plano selecionado
          </div>
          <div class="card-body">
            <div v-if="selectedPlan">
              <div class="row mb-3">
                <div class="col-3"><b>Nome</b></div>
                <div class="col-9">
                  {{ selectedPlan.name }}
                  (<router-link :to="{ path: '/billing/steps/plans' }">{{ $t('Alterar') }}</router-link>)  
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-3"><b>Ciclo</b></div>
                <div class="col-9">{{ cycleLabel(selectedPlan.cycle_type) }}</div>
              </div>
              <div class="row">
                <div class="col-3"><b>Valor</b></div>
                <div class="col-9">{{ $n(selectedPlan.price, {style: 'currency', currency: 'BRL'}) }}</div>
              </div>
              <div
                v-if="selectedPlan.first_invoice_discount"
                class="mt-3">
                <div class="row mb-3">
                  <div class="col-3"><b>Desconto</b></div>
                  <div class="col-9">-{{ $n(selectedPlan.first_invoice_discount, {style: 'currency', currency: 'BRL'}) }} (primeira fatura)</div>
                </div>
                <div class="row">
                  <div class="col-3"><b>Total</b></div>
                  <div class="col-9">{{ $n(selectedPlan.price - selectedPlan.first_invoice_discount, {style: 'currency', currency: 'BRL'}) }}</div>
                </div>
              </div>
            </div>
            <router-link
              v-if="!selectedPlan"
              :to="{ path: '/billing/steps/plans' }">
              {{ $t('Selecione um plano') }}
              </router-link>
          </div>
        </div>
        <div
          v-if="hasSubscription"
          class="card mb-3">
          <div class="card-header">
            Plano atual
          </div>
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-3"><b>Nome</b></div>
              <div class="col-9">{{ currentPlan.name }}</div>
            </div>
            <div class="row mb-3">
              <div class="col-3"><b>Ciclo</b></div>
              <div class="col-9">{{ cycleLabel(currentPlan.cycle_type) }}</div>
            </div>
            <div class="row mb-3">
              <div class="col-3"><b>Valor</b></div>
              <div class="col-9">{{ $n(currentPlan.value, {style: 'currency', currency: 'BRL'}) }}</div>
            </div>
          </div>
        </div>
        <div
          v-if="simulateNewPlan"
          class="card mb-3">
          <div class="card-header">
            Valor para troca de plano
          </div>
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-5"><b>Desconto</b></div>
              <div class="col-7">-{{ $n(simulateNewPlan.discount, {style: 'currency', currency: 'BRL'}) }}</div>
            </div>
            <div class="row mb-3">
              <div class="col-5"><b>Próxima fatura</b></div>
              <div class="col-7">{{ $d(new Date(simulateNewPlan.expires_at), 'short') }} ({{ simulateNewPlan.cycles }} ciclos)</div>
            </div>
            <div class="row mb-3">
              <div class="col-5"><b>Custo para mudança</b></div>
              <div class="col-7">{{ $n(simulateNewPlan.cost, {style: 'currency', currency: 'BRL'}) }}</div>
            </div>
          </div>
        </div>
        <div
          class="mt-4"
          :class="{'box-loading': loading}">
          <p>Ao "{{ hasSubscription ? 'atualizar a assinatura' : 'criar a assinatura' }}" você estará aceitando os nossos <a href="https://www.quickin.io/termos-e-condicoes/" target="_blank"><strong>Termos e Condições</strong></a> e nossa <a href="https://www.quickin.io/politica-de-privacidade/" target="_blank"><strong>Política de Privacidade</strong></a>.</p>
          <button
            type="button"
            class="btn btn-lg btn-primary btn-block"
            v-if="!hasSubscription"
            @click="create"
            :disabled="!hasAllData">Criar assinatura</button>
          <button
            type="button"
            class="btn btn-lg btn-primary btn-block"
            v-if="hasSubscription"
            @click="changePlan">Atualizar assinatura</button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
export default {
  name: 'BillingFinish',
  components: {
  },
  props: {
  },
  data () {
    return {
      loading: false,
      simulateNewPlan: null
    }
  },
  computed: {
    account () {
      return this.$store.getters.getAccount
    },
    selectedPlan () {
      return this.$store.getters.getBillingPlan
    },
    billingMode () {
      return this.selectedPlan.billing_mode
    },
    payableWith () {
      return this.selectedPlan.payable_with
    },
    payableWithCredcard () {
      return this.selectedPlan.payable_with.includes('credit_card')
    },
    payableWithBankSlip () {
      return this.selectedPlan.payable_with.includes('bank_slip')
    },
    isPostpaid () {
      return ['postpaid_bank_slip'].includes(this.selectedPlan.billing_mode)
    },
    billingResume () {
      return this.$store.getters.getBillingResume
    },
    hasCustomer () {
      return this.billingResume ? this.billingResume.has_customer : null
    },
    hasDefaultPaymentMethod () {
      return this.billingResume ? this.billingResume.has_default_paymethod : null
    },
    hasSubscription () {
      return this.billingResume ? this.billingResume.has_subscription : null
    },
    currentPlan () {
      return this.billingResume ? this.billingResume.plan : null
    },
    address () {
      return this.billingResume ? [
        this.billingResume.customer.address,
        this.billingResume.customer.address_number,
        this.billingResume.customer.address_complement,
        this.billingResume.customer.neighborhood,
        this.billingResume.customer.city,
        this.billingResume.customer.region,
        this.billingResume.customer.zipcode,
        this.billingResume.customer.country
      ].filter(v => v).join(', ') : null
    },
    hasAllData () {
      if (!this.hasCustomer) {
        return false
      }
      if (!this.selectedPlan) {
        return false
      }
      if (this.selectedPlan.type === 'prepaid_credcard' && !this.hasDefaultPaymentMethod) {
        return false
      }
      return true
    },
    isSamePlan () {
      return this.currentPlan && this.selectedPlan && this.currentPlan._id === this.selectedPlan.plan_id
    }
  },
  created () {
    this.simulateChangePlan()
  },
  methods: {
    simulateChangePlan () {
      this.loading = true
      this.$store.dispatch('getBillingSimulateChangePlan', this.selectedPlan.plan_id).then(res => {
        this.simulateNewPlan = res
      }).catch(() => {
        this.simulateNewPlan = null
      }).finally(() => {
        this.loading = false
      })
    },
    create () {
      this.loading = true
      this.$store.dispatch('createBillingSubscription', {
        plan_id: this.selectedPlan.plan_id
      }).then(() => {
        this.redirect()
      }).finally(() => {
        this.loading = false
      })
    },
    changePlan () {
      this.loading = true
      if (this.isSamePlan) {
        this.$store.dispatch('billingSubscriptionActivate').then(() => {
          this.redirect()
        }).finally(() => {
          this.loading = false
        })
      } else {
        this.$store.dispatch('updateBillingChangePlan', {
          plan_id: this.selectedPlan.plan_id
        }).then(() => {
          this.redirect()
        }).finally(() => {
          this.loading = false
        })
      }
    },
    redirect () {
      this.$store.dispatch('setBillingPlan', null)
      this.$store.dispatch('selectAccount', this.account._id)
      this.$router.push('/billing')
    },
    cycleLabel (cycle) {
      return { monthly: 'Mensal', annually: 'Mensal' }[cycle]
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Billing/BillingFinish.vue